import {
  GROUP_KINDS,
  LANGUAGE_VERSION,
  type AutoLine,
  type DynamicLine,
  type ExerciseNode,
  type Expr,
  type GroupItem,
  type Line,
  type Mods,
  type Prop,
  type SetLine,
  type Stmt,
  type TemplateNode,
  type Where,
  type WorkoutItem,
  type WorkoutNode,
} from './ast'
import { ScriptError, UNITS, isName, isPunct, tokenize, type Token } from './lexer'

/**
 * Source into a tree. The static tier is read by a plain recursive descent; expressions and
 * the statements inside a `dynamic` block are read by precedence climbing, and mean what
 * JavaScript means by them.
 */
export class Parser {
  private readonly tokens: Token[]
  private index = 0
  /** True inside a superset's braces, where `rest` belongs to the group and nowhere else. */
  private inSuperset = false

  constructor(source: string, allowHoles = false) {
    this.tokens = tokenize(source, allowHoles)
  }

  private get current(): Token {
    return this.tokenAt(this.index)
  }

  /** The token at a position, or the `end` the lexer always leaves behind past the last. */
  private tokenAt(index: number): Token {
    const token = this.tokens[index] ?? this.tokens[this.tokens.length - 1]
    if (token === undefined) {
      throw new ScriptError(1, 1, 'This file has nothing in it')
    }
    return token
  }

  private at(): Where {
    return { line: this.current.line, column: this.current.column }
  }

  private fail(message: string): never {
    throw new ScriptError(this.current.line, this.current.column, message)
  }

  private advance(): Token {
    const token = this.current
    this.index++
    return token
  }

  private skipBreaks(): void {
    while (this.current.kind === 'newline') {
      this.index++
    }
  }

  private expectPunct(value: string): Token {
    if (!isPunct(this.current, value)) {
      this.fail(`Expected \`${value}\`, found ${describe(this.current)}`)
    }
    return this.advance()
  }

  private expectName(): string {
    if (this.current.kind !== 'name') {
      this.fail(`Expected a name, found ${describe(this.current)}`)
    }
    return this.advance().text
  }

  private expectText(): string {
    if (this.current.kind !== 'text') {
      this.fail(`Expected a quoted name, found ${describe(this.current)}`)
    }
    return this.advance().text
  }

  // --- the file ---

  parseTemplate(): TemplateNode {
    this.skipBreaks()
    let version = LANGUAGE_VERSION
    if (isName(this.current, 'mixedscript')) {
      this.advance()
      if (this.current.kind !== 'number') {
        this.fail('The version line says which version of the language this is')
      }
      version = Math.trunc(this.advance().number)
      if (version > LANGUAGE_VERSION) {
        throw new ScriptError(
          1,
          1,
          `This template is written for MixedScript ${version}, and this app reads ${LANGUAGE_VERSION}`,
        )
      }
      this.skipBreaks()
    }

    const comments = this.current.comments
    const where = this.at()
    if (!isName(this.current, 'template')) {
      this.fail('A file holds one template, and starts with it')
    }
    this.advance()
    const name = this.expectText()
    const props = this.parseProps()
    const workouts: WorkoutNode[] = []
    this.parseBlock((token) => {
      if (isName(token, 'workout')) {
        workouts.push(this.parseWorkout())
      } else {
        this.fail(`A template holds workouts, and found ${describe(token)}`)
      }
    })
    this.skipBreaks()
    if (this.current.kind !== 'end') {
      this.fail('Nothing follows the template')
    }
    return { name, props, workouts, version, comments, at: where }
  }

  /** Runs `body` over each line of a `{ }`, taking care of the breaks and the closing brace. */
  private parseBlock(body: (token: Token) => void): void {
    this.skipBreaks()
    this.expectPunct('{')
    for (;;) {
      this.skipBreaks()
      if (isPunct(this.current, '}')) {
        this.advance()
        return
      }
      if (this.current.kind === 'end') {
        this.fail('This block is never closed')
      }
      body(this.current)
    }
  }

  private parseProps(): Prop[] {
    if (!isPunct(this.current, '(')) {
      return []
    }
    this.advance()
    const props: Prop[] = []
    for (;;) {
      this.skipBreaks()
      if (isPunct(this.current, ')')) {
        this.advance()
        return props
      }
      const where = this.at()
      const name = this.expectName()
      this.expectPunct(':')
      const value = this.parseExpr()
      if (props.some((prop) => prop.name === name)) {
        throw new ScriptError(where.line, where.column, `\`${name}\` is given twice`)
      }
      props.push({ name, value, at: where })
      this.skipBreaks()
      if (isPunct(this.current, ',')) {
        this.advance()
      }
    }
  }

  private parseWorkout(): WorkoutNode {
    const comments = this.current.comments
    const where = this.at()
    this.advance()
    const name = this.expectText()
    const props = this.parseProps()
    const items: WorkoutItem[] = []
    this.parseBlock((token) => {
      if (isName(token, 'exercise')) {
        items.push({ type: 'exercise', exercise: this.parseExercise() })
      } else if (GROUP_KINDS.some((kind) => isName(token, kind))) {
        items.push(this.parseGroup())
      } else {
        this.fail(`A workout holds exercises and groups, and found ${describe(token)}`)
      }
    })
    return { name, props, items, comments, at: where }
  }

  private parseGroup(): GroupItem {
    const comments = this.current.comments
    const where = this.at()
    const kind = GROUP_KINDS.find((candidate) => isName(this.current, candidate))
    if (kind === undefined) {
      this.fail(`Expected a group, found ${describe(this.current)}`)
    }
    this.advance()
    const props = this.parseProps()
    const first = props[0]
    if (kind !== 'superset' && first !== undefined) {
      throw new ScriptError(first.at.line, first.at.column, `A ${kind} takes no properties`)
    }
    const stray = props.find((prop) => prop.name !== 'rest')
    if (stray) {
      throw new ScriptError(stray.at.line, stray.at.column, 'A superset takes only `rest`')
    }
    const exercises: ExerciseNode[] = []
    const within = this.inSuperset
    this.inSuperset = kind === 'superset'
    try {
      this.parseBlock((token) => {
        if (isName(token, 'exercise')) {
          exercises.push(this.parseExercise())
        } else {
          this.fail(`A ${kind} holds exercises, and found ${describe(token)}`)
        }
      })
    } finally {
      this.inSuperset = within
    }
    if (exercises.length === 0) {
      throw new ScriptError(where.line, where.column, `A ${kind} with no exercises does nothing`)
    }
    return { type: 'group', kind, exercises, props, comments, at: where }
  }

  private parseExercise(): ExerciseNode {
    const comments = this.current.comments
    const where = this.at()
    this.advance()
    const name = this.expectText()
    const props = this.parseProps()
    if (this.inSuperset) {
      const rest = props.find((prop) => prop.name === 'rest')
      if (rest) {
        this.banSupersetRest(rest.at)
      }
    }
    const lines: Line[] = []
    this.parseBlock(() => {
      lines.push(this.parseLine())
    })
    return { name, props, lines, comments, at: where }
  }

  private banSupersetRest(at: Where): never {
    throw new ScriptError(at.line, at.column, 'Inside a superset, rest lives on the superset')
  }

  // --- set lines ---

  private parseLine(): Line {
    const comments = this.current.comments
    const where = this.at()

    let warmup = false
    if (isName(this.current, 'warmup')) {
      this.advance()
      warmup = true
    }

    if (isName(this.current, 'dynamic')) {
      return this.parseDynamic(warmup, comments, where)
    }

    if (isName(this.current, 'auto') && isPunct(this.tokenAt(this.index + 1), '(')) {
      this.advance()
      this.expectPunct('(')
      const args: [string, Expr][] = []
      while (!isPunct(this.current, ')')) {
        this.skipBreaks()
        const name = this.expectName()
        this.expectPunct(':')
        args.push([name, this.parseExpr()])
        this.skipBreaks()
        if (isPunct(this.current, ',')) {
          this.advance()
        }
      }
      this.expectPunct(')')
      const line: AutoLine = {
        type: 'auto',
        warmup,
        args,
        mods: this.parseMods(),
        comments,
        at: where,
      }
      return line
    }

    const first = this.parseExpr()
    let count: Expr | null = null
    let primary = first
    if (isName(this.current, 'x')) {
      this.advance()
      count = first
      primary = this.parseExpr()
    }

    let weight: Expr | null = null
    if (isPunct(this.current, '@')) {
      this.advance()
      weight = this.parseExpr()
    }

    const line: SetLine = {
      type: 'set',
      warmup,
      count,
      primary,
      weight,
      mods: this.parseMods(),
      comments,
      at: where,
    }
    return line
  }

  /** The trailing part of a set line: rest, video, a note. Order does not matter. */
  private parseMods(): Mods {
    let rest: Expr | undefined
    let video: boolean | undefined
    let notes: string | undefined
    for (;;) {
      if (isName(this.current, 'rest')) {
        if (this.inSuperset) {
          this.banSupersetRest(this.at())
        }
        this.advance()
        rest = this.parseExpr()
      } else if (isName(this.current, 'video')) {
        this.advance()
        video = true
      } else if (this.current.kind === 'text') {
        notes = this.advance().text
      } else {
        this.endOfLine()
        return { rest, video, notes }
      }
    }
  }

  private endOfLine(): void {
    if (this.current.kind === 'newline') {
      this.advance()
    } else if (!isPunct(this.current, '}') && this.current.kind !== 'end') {
      this.fail(`Nothing else belongs on this line, and found ${describe(this.current)}`)
    }
  }

  private parseDynamic(warmup: boolean, comments: readonly string[], where: Where): DynamicLine {
    if (warmup) {
      throw new ScriptError(
        where.line,
        where.column,
        'A block says on the sets it yields whether they are warm-ups',
      )
    }
    this.advance()
    const label = this.current.kind === 'text' ? this.advance().text : ''
    this.skipBreaks()
    const body = this.parseStatements()
    return { type: 'dynamic', label, body, comments, at: where }
  }

  // --- statements ---

  private parseStatements(): Stmt[] {
    const statements: Stmt[] = []
    this.expectPunct('{')
    for (;;) {
      this.skipBreaks()
      if (isPunct(this.current, '}')) {
        this.advance()
        return statements
      }
      if (this.current.kind === 'end') {
        this.fail('This block is never closed')
      }
      statements.push(this.parseStatement())
    }
  }

  /** A `{ }` of statements, or the single statement that stands where one would. */
  private parseBody(): Stmt[] {
    this.skipBreaks()
    return isPunct(this.current, '{') ? this.parseStatements() : [this.parseStatement()]
  }

  private parseStatement(): Stmt {
    const where = this.at()

    if (isName(this.current, 'let') || isName(this.current, 'const')) {
      const mutable = this.advance().text === 'let'
      const name = this.expectName()
      this.expectPunct('=')
      const value = this.parseExpr()
      this.endOfStatement()
      return { type: 'declare', mutable, name, value, at: where }
    }

    if (isName(this.current, 'if')) {
      this.advance()
      this.expectPunct('(')
      const condition = this.parseExpr()
      this.expectPunct(')')
      const then = this.parseBody()
      this.skipBreaks()
      let otherwise: Stmt[] = []
      if (isName(this.current, 'else')) {
        this.advance()
        this.skipBreaks()
        otherwise = isName(this.current, 'if') ? [this.parseStatement()] : this.parseBody()
      }
      return { type: 'if', condition, then, otherwise, at: where }
    }

    if (isName(this.current, 'for')) {
      return this.parseFor(where)
    }

    if (isName(this.current, 'yield')) {
      this.advance()
      const line = this.parseLine()
      if (line.type !== 'set') {
        throw new ScriptError(where.line, where.column, 'A block yields set lines')
      }
      return { type: 'yield', line, at: where }
    }

    if (isName(this.current, 'return')) {
      this.advance()
      if (
        this.current.kind === 'newline' ||
        isPunct(this.current, '}') ||
        isPunct(this.current, ';')
      ) {
        this.endOfStatement()
        return { type: 'return', line: null, at: where }
      }
      const line = this.parseLine()
      if (line.type !== 'set') {
        throw new ScriptError(where.line, where.column, 'A block returns a set line, or nothing')
      }
      return { type: 'return', line, at: where }
    }

    const target = this.parseExpr()
    const assignment = ['=', '+=', '-=', '*=', '/='].find((op) => isPunct(this.current, op))
    if (assignment !== undefined) {
      this.advance()
      const value = this.parseExpr()
      this.endOfStatement()
      return { type: 'assign', target, op: assignment, value, at: where }
    }
    if (isPunct(this.current, '++') || isPunct(this.current, '--')) {
      const op = this.advance().text
      this.endOfStatement()
      return {
        type: 'assign',
        target,
        op: op === '++' ? '+=' : '-=',
        value: { type: 'number', value: 1, unit: UNITS.reps, at: where },
        at: where,
      }
    }
    this.endOfStatement()
    return { type: 'expr', expr: target, at: where }
  }

  private parseFor(where: Where): Stmt {
    this.advance()
    this.expectPunct('(')
    // `for (const s of sessions)` walks the values; the counting form is the other one.
    if (
      (isName(this.current, 'const') || isName(this.current, 'let')) &&
      isName(this.tokenAt(this.index + 2), 'of')
    ) {
      this.advance()
      const name = this.expectName()
      this.advance()
      const source = this.parseExpr()
      this.expectPunct(')')
      return { type: 'forOf', name, source, body: this.parseBody(), at: where }
    }
    const init = isPunct(this.current, ';') ? null : this.parseStatement()
    if (isPunct(this.current, ';')) {
      this.advance()
    }
    const condition = isPunct(this.current, ';') ? null : this.parseExpr()
    this.expectPunct(';')
    const step = isPunct(this.current, ')') ? null : this.parseStatement()
    this.expectPunct(')')
    return { type: 'for', init, condition, step, body: this.parseBody(), at: where }
  }

  private endOfStatement(): void {
    if (isPunct(this.current, ';')) {
      this.advance()
    }
    if (this.current.kind === 'newline') {
      this.advance()
    }
  }

  // --- expressions ---

  parseExpr(): Expr {
    return this.parseTernary()
  }

  private parseTernary(): Expr {
    const condition = this.parseBinary(0)
    if (!isPunct(this.current, '?')) {
      return condition
    }
    const where = this.at()
    this.advance()
    const then = this.parseExpr()
    this.expectPunct(':')
    return { type: 'ternary', condition, then, otherwise: this.parseExpr(), at: where }
  }

  /** Lowest binding first, so the table reads the way the precedence does. */
  private static readonly levels: readonly (readonly string[])[] = [
    ['??'],
    ['||'],
    ['&&'],
    ['===', '!=='],
    ['<', '>', '<=', '>='],
    ['+', '-'],
    ['*', '/', '%'],
  ]

  private parseBinary(level: number): Expr {
    if (level >= Parser.levels.length) {
      return this.parseUnary()
    }
    let left = this.parseBinary(level + 1)
    for (;;) {
      if (isPunct(this.current, '==') || isPunct(this.current, '!=')) {
        this.fail('Use `===` and `!==`; `==` and `!=` are not part of the language')
      }
      const op = (Parser.levels[level] ?? []).find((candidate) => isPunct(this.current, candidate))
      if (op === undefined) {
        return left
      }
      const where = this.at()
      this.advance()
      this.skipBreaks()
      left = { type: 'binary', op, left, right: this.parseBinary(level + 1), at: where }
    }
  }

  private parseUnary(): Expr {
    if (isPunct(this.current, '!') || isPunct(this.current, '-')) {
      const where = this.at()
      const op = this.advance().text
      return { type: 'unary', op, operand: this.parseUnary(), at: where }
    }
    return this.parsePostfix(this.parsePrimary())
  }

  private parsePostfix(start: Expr): Expr {
    let target = start
    for (;;) {
      if (isPunct(this.current, '.') || isPunct(this.current, '?.')) {
        const optional = this.current.text === '?.'
        const where = this.at()
        this.advance()
        target = { type: 'member', target, name: this.expectName(), optional, at: where }
      } else if (isPunct(this.current, '[')) {
        const where = this.at()
        this.advance()
        const index = this.parseExpr()
        this.expectPunct(']')
        target = { type: 'index', target, index, at: where }
      } else if (isPunct(this.current, '(')) {
        const where = this.at()
        this.advance()
        const args: Expr[] = []
        const named: [string, Expr][] = []
        while (!isPunct(this.current, ')')) {
          this.skipBreaks()
          if (this.current.kind === 'name' && isPunct(this.tokenAt(this.index + 1), ':')) {
            const name = this.expectName()
            this.advance()
            named.push([name, this.parseExpr()])
          } else {
            args.push(this.parseExpr())
          }
          this.skipBreaks()
          if (isPunct(this.current, ',')) {
            this.advance()
          }
        }
        this.expectPunct(')')
        target = { type: 'call', callee: target, args, named, at: where }
      } else {
        return target
      }
    }
  }

  private parsePrimary(): Expr {
    const where = this.at()
    const token = this.current

    if (token.kind === 'number') {
      this.advance()
      return { type: 'number', value: token.number, unit: token.unit, at: where }
    }
    if (token.kind === 'text') {
      this.advance()
      return { type: 'text', value: token.text, at: where }
    }
    if (token.kind === 'hole') {
      this.advance()
      return { type: 'hole', name: token.text, at: where }
    }
    if (isPunct(token, '[')) {
      this.advance()
      const items: Expr[] = []
      for (;;) {
        this.skipBreaks()
        if (isPunct(this.current, ']')) {
          this.advance()
          return { type: 'array', items, at: where }
        }
        items.push(this.parseExpr())
        this.skipBreaks()
        if (isPunct(this.current, ',')) {
          this.advance()
        }
      }
    }
    if (isPunct(token, '(')) {
      const arrow = this.arrowAhead()
      if (arrow) {
        return arrow
      }
      this.advance()
      const inner = this.parseExpr()
      this.expectPunct(')')
      return inner
    }
    if (token.kind === 'name') {
      if (isPunct(this.tokenAt(this.index + 1), '=>')) {
        this.advance()
        this.advance()
        return { type: 'arrow', params: [token.text], body: this.parseExpr(), at: where }
      }
      this.advance()
      return { type: 'name', name: token.text, at: where }
    }
    this.fail(`Expected a value, found ${describe(token)}`)
  }

  /** `(a, b) => …` — told apart from a bracketed expression by looking for the arrow. */
  private arrowAhead(): Expr | null {
    let depth = 0
    let scan = this.index
    while (scan < this.tokens.length) {
      const token = this.tokenAt(scan)
      if (isPunct(token, '(')) depth++
      if (isPunct(token, ')')) {
        depth--
        if (depth === 0) {
          break
        }
      }
      scan++
    }
    const after = this.tokens[scan + 1]
    if (!after || !isPunct(after, '=>')) {
      return null
    }
    const where = this.at()
    this.advance()
    const params: string[] = []
    while (!isPunct(this.current, ')')) {
      params.push(this.expectName())
      if (isPunct(this.current, ',')) {
        this.advance()
      }
    }
    this.expectPunct(')')
    this.expectPunct('=>')
    return { type: 'arrow', params, body: this.parseExpr(), at: where }
  }
}

function describe(token: Token): string {
  switch (token.kind) {
    case 'end':
      return 'the end of the file'
    case 'newline':
      return 'the end of the line'
    case 'text':
      return 'text'
    default:
      return `\`${token.text}\``
  }
}

/** Reads a whole template, or says why it cannot. */
export function parseTemplate(source: string): TemplateNode {
  return new Parser(source).parseTemplate()
}
